import {
  StateEffect,
  StateField,
  type Extension,
  type Range,
} from '@codemirror/state';
import { Decoration, type DecorationSet, EditorView } from '@codemirror/view';

/**
 * StateEffect to set which line range should be highlighted as the "current chunk".
 * Pass { from: -1, to: -1 } to clear the highlight.
 */
export const setChunkHighlight = StateEffect.define<{
  from: number;
  to: number;
}>();

const currentChunkMark = Decoration.line({ class: 'cm-currentChunk' });

/**
 * StateField that manages line decorations for the currently focused chunk.
 */
const chunkHighlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setChunkHighlight)) {
        const { from, to } = effect.value;
        if (from < 0) return Decoration.none;

        const doc = tr.state.doc;
        const marks: Range<Decoration>[] = [];
        const endPos = Math.min(to, doc.length);

        // Decorate each line in the chunk range
        for (let pos = from; pos <= endPos; ) {
          const line = doc.lineAt(pos);
          marks.push(currentChunkMark.range(line.from));
          pos = line.to + 1;
        }

        return Decoration.set(marks);
      }
    }
    return decorations;
  },
  provide: (field) => EditorView.decorations.from(field),
});

/**
 * Theme for the current-chunk highlight.
 * Uses a prominent left border and slightly boosted background.
 */
const currentChunkTheme = EditorView.baseTheme({
  '.cm-currentChunk': {
    outline: '2px solid rgba(100, 153, 255, 0.6)',
    outlineOffset: '-2px',
    backgroundColor: 'rgba(100, 153, 255, 0.08)',
  },
});

/**
 * Extension bundle: include this in your editor to enable chunk highlighting.
 * Then dispatch `setChunkHighlight` effects to highlight/clear ranges.
 */
export const chunkHighlightExtension: Extension = [
  chunkHighlightField,
  currentChunkTheme,
];
